import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Pencil, ArrowRight, X as XIcon } from 'lucide-react';

import { Button } from './ui/button';

const QUIZ_KEY = 'vitala_saved_assessment';
const EDIT_KEY_PREFIX = 'vitala:edit-answers-draft:';
const QUIZ_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const EDIT_TTL_MS = 14 * 24 * 60 * 60 * 1000;

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / (60 * 24))}d ago`;
}

// Small card on the Dashboard that surfaces any in-progress quiz or edit-answers
// drafts saved in localStorage, with one-tap Resume + Discard.
export default function InProgressWidget({ userId }) {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);

  const refresh = () => {
    const out = [];
    // Quiz draft
    try {
      const raw = localStorage.getItem(QUIZ_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        const savedAt = new Date(d.savedAt).getTime();
        if (savedAt && Date.now() - savedAt <= QUIZ_TTL_MS) {
          out.push({
            id: 'quiz',
            type: 'quiz',
            title: 'Health assessment in progress',
            subtitle: `${Object.keys(d.answers || {}).length} answer${Object.keys(d.answers || {}).length !== 1 ? 's' : ''} saved · ${timeAgo(d.savedAt)}`,
            cta: 'Resume quiz',
            onResume: () => navigate('/quiz'),
            onDiscard: () => { localStorage.removeItem(QUIZ_KEY); refresh(); },
          });
        }
      }
    } catch { /* ignore */ }

    // Edit-answers draft (per-user)
    if (userId) {
      try {
        const raw = localStorage.getItem(EDIT_KEY_PREFIX + userId);
        if (raw) {
          const d = JSON.parse(raw);
          const savedAt = new Date(d.savedAt).getTime();
          if (savedAt && Date.now() - savedAt <= EDIT_TTL_MS && d.answers) {
            out.push({
              id: 'edit',
              type: 'edit',
              title: 'Unfinished answer edits',
              subtitle: `${Object.keys(d.answers || {}).length} answers · ${timeAgo(d.savedAt)}`,
              cta: 'Resume editing',
              onResume: () => navigate('/edit-answers'),
              onDiscard: () => { localStorage.removeItem(EDIT_KEY_PREFIX + userId); refresh(); },
            });
          }
        }
      } catch { /* ignore */ }
    }

    setDrafts(out);
  };

  useEffect(() => {
    refresh();
    // Refresh when the user comes back to the tab — keeps the card in sync
    // if the draft was edited or completed elsewhere.
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (drafts.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="in-progress-widget">
      {drafts.map(d => {
        const isQuiz = d.type === 'quiz';
        const Icon = isQuiz ? Sparkles : Pencil;
        const tint = isQuiz
          ? 'border-emerald-300 bg-emerald-50'
          : 'border-amber-300 bg-amber-50';
        const iconBg = isQuiz ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
        const ctaBg = isQuiz ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700';
        return (
          <motion.div
            key={d.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-xl border ${tint} p-3 sm:p-4 flex items-center gap-3`}
            data-testid={`in-progress-${d.id}`}
          >
            <div className={`w-9 h-9 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#1A1A1A] truncate">{d.title}</p>
              <p className="text-[11px] text-muted-foreground truncate">{d.subtitle}</p>
            </div>
            <Button
              size="sm"
              onClick={d.onResume}
              className={`${ctaBg} text-white shrink-0 h-8 text-xs`}
              data-testid={`in-progress-resume-${d.id}`}
            >
              {d.cta}
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
            <button
              onClick={d.onDiscard}
              className="p-1 rounded hover:bg-black/5 text-muted-foreground shrink-0"
              title="Discard draft"
              data-testid={`in-progress-discard-${d.id}`}
            >
              <XIcon className="w-4 h-4" />
            </button>
          </motion.div>
        );
      })}
    </div>
  );
}
